"use client"

import { useState, useEffect } from "react"
import axios from "axios"
import config from "../config"
import { Line, Doughnut } from "react-chartjs-2"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js"
import {
  TrendingUp,
  Users,
  Eye,
  DollarSign,
  PieChart,
  BarChart3,
  Calendar,
  Target,
  AlertTriangle,
  Clock,
  Smartphone,
  Monitor,
  Tablet,
  Download,
  RefreshCw,
} from "lucide-react"

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
)

const EventAnalytics = ({ event, isCreator }) => {
  const [analytics, setAnalytics] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState("overview")
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    if (isCreator && event?.slug) {
      fetchAnalytics(event.slug)
    }
  }, [event, isCreator])

  const fetchAnalytics = async (slug) => {
    try {
      setLoading(true)
      setError(null)
      const response = await axios.get(`${config.API_BASE_URL}/events/${slug}/analytics/`)
      setAnalytics(response.data)
    } catch (error) {
      // console.error("Error fetching analytics:", error)
      setError(error.response?.data?.error || "Failed to load analytics")
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    await fetchAnalytics(event.slug)
    setRefreshing(false)
  }

  const exportData = () => {
    // Export analytics data as CSV
    const csvData = [
      ["Metric", "Value"],
      ["Total Views", analytics.total_views || 0],
      ["Unique Visitors", analytics.unique_visitors || 0],
      ["Total Guests", analytics.guests_count || 0],
      ["Total Budget", analytics.budget?.total_budget || 0],
      ["Remaining Budget", analytics.budget?.remaining_budget || 0],
      ["Budget Completion", `${analytics.budget?.budget_completion?.toFixed(1) || 0}%`],
    ]

    const csvContent = csvData.map((row) => row.join(",")).join("\n")
    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = `${event.title}_analytics.csv`
    link.click()
  }

  if (!isCreator) {
    return null
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-20 w-20 border-4 border-indigo-200 mx-auto"></div>
            <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-indigo-600 mx-auto absolute top-0"></div>
          </div>
          <p className="mt-8 text-gray-700 text-xl font-semibold">Loading analytics...</p>
          <p className="mt-2 text-gray-500">Analyzing event performance</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md text-center">
          <div className="mx-auto h-16 w-16 text-red-500 mb-4">
            <AlertTriangle className="w-full h-full" />
          </div>
          <h2 className="text-2xl font-bold text-red-600 mb-4">Analytics Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => fetchAnalytics(event.slug)}
            className="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors duration-200 font-semibold"
          >
            Retry Loading
          </button>
        </div>
      </div>
    )
  }

  if (!analytics) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md text-center">
          <div className="mx-auto h-16 w-16 text-gray-400 mb-4">
            <BarChart3 className="w-full h-full" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">No Analytics Data</h2>
          <p className="text-gray-600">Analytics data is not available for this event yet.</p>
        </div>
      </div>
    )
  }

  // Enhanced chart configurations
  const viewsData = {
    labels: analytics?.views_by_date?.map((view) => new Date(view.date).toLocaleDateString()) || [],
    datasets: [
      {
        label: "Page Views",
        data: analytics?.views_by_date?.map((view) => view.count) || [],
        borderColor: "rgb(99, 102, 241)",
        backgroundColor: "rgba(99, 102, 241, 0.1)",
        tension: 0.4,
        fill: true,
        pointBackgroundColor: "rgb(99, 102, 241)",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
      },
    ],
  }

  const deviceData = {
    labels:
      analytics?.views_by_device?.map((device) => {
        const agent = device.user_agent.toLowerCase()
        if (agent.includes("mobile")) return "Mobile"
        if (agent.includes("tablet")) return "Tablet"
        return "Desktop"
      }) || [],
    datasets: [
      {
        data: analytics?.views_by_device?.map((device) => device.count) || [],
        backgroundColor: [
          "rgba(99, 102, 241, 0.8)",
          "rgba(16, 185, 129, 0.8)",
          "rgba(245, 158, 11, 0.8)",
          "rgba(239, 68, 68, 0.8)",
          "rgba(139, 92, 246, 0.8)",
        ],
        borderColor: [
          "rgb(99, 102, 241)",
          "rgb(16, 185, 129)",
          "rgb(245, 158, 11)",
          "rgb(239, 68, 68)",
          "rgb(139, 92, 246)",
        ],
        borderWidth: 2,
        hoverOffset: 10,
      },
    ],
  }

  const expenseData = {
    labels: analytics.budget?.expense_breakdown?.map((expense) => expense.category__name) || [],
    datasets: [
      {
        data: analytics.budget?.expense_breakdown?.map((expense) => expense.total) || [],
        backgroundColor: [
          "rgba(99, 102, 241, 0.8)",
          "rgba(16, 185, 129, 0.8)",
          "rgba(245, 158, 11, 0.8)",
          "rgba(239, 68, 68, 0.8)",
          "rgba(139, 92, 246, 0.8)",
          "rgba(236, 72, 153, 0.8)",
        ],
        borderColor: [
          "rgb(99, 102, 241)",
          "rgb(16, 185, 129)",
          "rgb(245, 158, 11)",
          "rgb(239, 68, 68)",
          "rgb(139, 92, 246)",
          "rgb(236, 72, 153)",
        ],
        borderWidth: 2,
        hoverOffset: 10,
      },
    ],
  }

  const getDeviceIcon = (userAgent) => {
    const agent = userAgent.toLowerCase()
    if (agent.includes("mobile")) return <Smartphone className="h-5 w-5 text-blue-600" />
    if (agent.includes("tablet")) return <Tablet className="h-5 w-5 text-green-600" />
    return <Monitor className="h-5 w-5 text-purple-600" />
  }

  const getBudgetStatusColor = (status) => {
    switch (status) {
      case "draft":
        return "bg-yellow-100 text-yellow-800 border-yellow-200"
      case "approved":
        return "bg-green-100 text-green-800 border-green-200"
      case "completed":
        return "bg-blue-100 text-blue-800 border-blue-200"
      default:
        return "bg-red-100 text-red-800 border-red-200"
    }
  }

  const tabs = [
    { id: "overview", name: "Overview", icon: BarChart3 },
    { id: "engagement", name: "Engagement", icon: TrendingUp },
    { id: "budget", name: "Budget", icon: DollarSign },
    { id: "devices", name: "Devices", icon: Smartphone },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50">
      {/* Header */}
      <div className="bg-white shadow-xl border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-8">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Event Analytics
              </h1>
              <p className="mt-3 text-gray-600 text-lg">
                Comprehensive insights for <span className="font-semibold text-indigo-600">{event.title}</span>
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="inline-flex items-center px-6 py-3 border border-gray-300 rounded-xl text-gray-700 bg-white hover:bg-gray-50 font-medium transition-colors duration-200 disabled:opacity-50"
              >
                <RefreshCw className={`-ml-1 mr-2 h-5 w-5 ${refreshing ? "animate-spin" : ""}`} />
                {refreshing ? "Refreshing..." : "Refresh"}
              </button>
              <button
                onClick={exportData}
                className="inline-flex items-center px-6 py-3 border border-transparent rounded-xl text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 font-medium transition-colors duration-200 shadow-lg"
              >
                <Download className="-ml-1 mr-2 h-5 w-5" />
                Export Data
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200 bg-white rounded-t-2xl shadow-lg">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors duration-200 ${activeTab === tab.id
                      ? "border-indigo-500 text-indigo-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{tab.name}</span>
                  </button>
                )
              })}
            </nav>
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-8">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-16 w-16 bg-blue-100 rounded-2xl flex items-center justify-center shadow-lg">
                      <Eye className="h-8 w-8 text-blue-600" />
                    </div>
                  </div>
                  <div className="ml-6">
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Total Views</p>
                    <p className="text-3xl font-bold text-gray-900">{analytics.total_views?.toLocaleString() || 0}</p>
                    <p className="text-sm text-blue-600 font-medium">+12% from last week</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-16 w-16 bg-green-100 rounded-2xl flex items-center justify-center shadow-lg">
                      <Users className="h-8 w-8 text-green-600" />
                    </div>
                  </div>
                  <div className="ml-6">
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Unique Visitors</p>
                    <p className="text-3xl font-bold text-gray-900">
                      {analytics.unique_visitors?.toLocaleString() || 0}
                    </p>
                    <p className="text-sm text-green-600 font-medium">+8% from last week</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-16 w-16 bg-purple-100 rounded-2xl flex items-center justify-center shadow-lg">
                      <Calendar className="h-8 w-8 text-purple-600" />
                    </div>
                  </div>
                  <div className="ml-6">
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Total Guests</p>
                    <p className="text-3xl font-bold text-gray-900">{analytics.guests_count?.toLocaleString() || 0}</p>
                    <p className="text-sm text-purple-600 font-medium">Confirmed attendees</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-16 w-16 bg-yellow-100 rounded-2xl flex items-center justify-center shadow-lg">
                      <DollarSign className="h-8 w-8 text-yellow-600" />
                    </div>
                  </div>
                  <div className="ml-6">
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Budget Used</p>
                    <p className="text-3xl font-bold text-gray-900">
                      {analytics.budget?.budget_completion?.toFixed(1) || 0}%
                    </p>
                    <p className="text-sm text-yellow-600 font-medium">Of total budget</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Views Over Time</h3>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-indigo-500 rounded-full"></div>
                    <span className="text-sm text-gray-600">Page Views</span>
                  </div>
                </div>
                <div className="h-80">
                  <Line
                    data={viewsData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false,
                        },
                        tooltip: {
                          backgroundColor: "rgba(0, 0, 0, 0.8)",
                          titleColor: "#fff",
                          bodyColor: "#fff",
                          borderColor: "rgb(99, 102, 241)",
                          borderWidth: 1,
                          cornerRadius: 8,
                        },
                      },
                      scales: {
                        x: {
                          grid: {
                            display: false,
                          },
                          ticks: {
                            color: "#6b7280",
                          },
                        },
                        y: {
                          beginAtZero: true,
                          grid: {
                            color: "#f3f4f6",
                          },
                          ticks: {
                            color: "#6b7280",
                            stepSize: Math.ceil((analytics.total_views || 0) / 5),
                          },
                        },
                      },
                      elements: {
                        point: {
                          hoverBackgroundColor: "#fff",
                        },
                      },
                    }}
                  />
                </div>
              </div>

              <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Device Distribution</h3>
                  <PieChart className="h-5 w-5 text-gray-400" />
                </div>
                <div className="h-80">
                  <Doughnut
                    data={deviceData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: "bottom",
                          labels: {
                            padding: 20,
                            usePointStyle: true,
                            font: {
                              size: 12,
                            },
                          },
                        },
                        tooltip: {
                          backgroundColor: "rgba(0, 0, 0, 0.8)",
                          titleColor: "#fff",
                          bodyColor: "#fff",
                          cornerRadius: 8,
                        },
                      },
                      cutout: "60%",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Engagement Tab */}
        {activeTab === "engagement" && (
          <div className="space-y-8">
            <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Engagement Metrics</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl">
                  <div className="mx-auto h-12 w-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                    <TrendingUp className="h-6 w-6 text-blue-600" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Engagement Rate</h4>
                  <p className="text-3xl font-bold text-blue-600">
                    {((analytics.unique_visitors / analytics.total_views) * 100).toFixed(1)}%
                  </p>
                </div>
                <div className="text-center p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl">
                  <div className="mx-auto h-12 w-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                    <Users className="h-6 w-6 text-green-600" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Return Visitors</h4>
                  <p className="text-3xl font-bold text-green-600">
                    {analytics.total_views - analytics.unique_visitors}
                  </p>
                </div>
                <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-violet-50 rounded-2xl">
                  <div className="mx-auto h-12 w-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                    <Eye className="h-6 w-6 text-purple-600" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Avg. Views/Visitor</h4>
                  <p className="text-3xl font-bold text-purple-600">
                    {(analytics.total_views / analytics.unique_visitors).toFixed(1)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Views Timeline</h3>
              <div className="h-96">
                <Line
                  data={viewsData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    scales: {
                      x: {
                        grid: {
                          display: false,
                        },
                      },
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: "#f3f4f6",
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Budget Tab */}
        {activeTab === "budget" && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Budget Overview</h3>
                  <Target className="h-6 w-6 text-gray-400" />
                </div>
                <div className="space-y-6">
                  <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                    <span className="text-gray-600 font-medium">Total Budget</span>
                    <span className="text-xl font-bold text-gray-900">
                      KES {analytics.budget?.total_budget?.toLocaleString() || 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-green-50 rounded-xl">
                    <span className="text-gray-600 font-medium">Remaining Budget</span>
                    <span className="text-xl font-bold text-green-600">
                      KES {analytics.budget?.remaining_budget?.toLocaleString() || 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-blue-50 rounded-xl">
                    <span className="text-gray-600 font-medium">Budget Completion</span>
                    <span className="text-xl font-bold text-blue-600">
                      {analytics.budget?.budget_completion?.toFixed(1) || 0}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                    <span className="text-gray-600 font-medium">Status</span>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-semibold border ${getBudgetStatusColor(analytics.budget?.status)}`}
                    >
                      {analytics.budget?.status?.charAt(0).toUpperCase() + analytics.budget?.status?.slice(1)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-yellow-50 rounded-xl">
                      <Clock className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Pending Expenses</p>
                      <p className="text-2xl font-bold text-yellow-600">{analytics.budget?.pending_expenses || 0}</p>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded-xl">
                      <AlertTriangle className="h-8 w-8 text-red-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Over Budget</p>
                      <p className="text-2xl font-bold text-red-600">{analytics.budget?.over_budget_expenses || 0}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Expense Breakdown</h3>
                  <PieChart className="h-6 w-6 text-gray-400" />
                </div>
                <div className="h-80">
                  <Doughnut
                    data={expenseData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: "bottom",
                          labels: {
                            padding: 20,
                            usePointStyle: true,
                            font: {
                              size: 12,
                            },
                          },
                        },
                        tooltip: {
                          backgroundColor: "rgba(0, 0, 0, 0.8)",
                          titleColor: "#fff",
                          bodyColor: "#fff",
                          cornerRadius: 8,
                          callbacks: {
                            label: (context) => `${context.label}: KES ${context.parsed.toLocaleString()}`,
                          },
                        },
                      },
                      cutout: "60%",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Budget Progress Bar */}
            <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Budget Progress</h3>
              <div className="space-y-4">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>
                    Used: KES{" "}
                    {(analytics.budget?.total_budget - analytics.budget?.remaining_budget)?.toLocaleString() || 0}
                  </span>
                  <span>Remaining: KES {analytics.budget?.remaining_budget?.toLocaleString() || 0}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className="bg-gradient-to-r from-indigo-500 to-purple-600 h-4 rounded-full transition-all duration-500"
                    style={{ width: `${analytics.budget?.budget_completion || 0}%` }}
                  ></div>
                </div>
                <div className="text-center">
                  <span className="text-2xl font-bold text-gray-900">
                    {analytics.budget?.budget_completion?.toFixed(1) || 0}% Complete
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Devices Tab */}
        {activeTab === "devices" && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Device Distribution</h3>
                  <Smartphone className="h-6 w-6 text-gray-400" />
                </div>
                <div className="h-80">
                  <Doughnut
                    data={deviceData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: "bottom",
                          labels: {
                            padding: 20,
                            usePointStyle: true,
                            font: {
                              size: 14,
                            },
                          },
                        },
                      },
                      cutout: "50%",
                    }}
                  />
                </div>
              </div>

              <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Device Details</h3>
                <div className="space-y-4">
                  {analytics?.views_by_device?.map((device, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-200"
                    >
                      <div className="flex items-center space-x-3">
                        {getDeviceIcon(device.user_agent)}
                        <div>
                          <p className="font-medium text-gray-900">
                            {device.user_agent.toLowerCase().includes("mobile")
                              ? "Mobile"
                              : device.user_agent.toLowerCase().includes("tablet")
                                ? "Tablet"
                                : "Desktop"}
                          </p>
                          <p className="text-sm text-gray-500">{device.user_agent}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-indigo-600">{device.count}</p>
                        <p className="text-sm text-gray-500">
                          {((device.count / analytics.total_views) * 100).toFixed(1)}%
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}

export default EventAnalytics